import React, { useEffect, useState } from "react";
import "./EditNotice.scss";
import { BASE_URL, IMG_URL } from "../../../utils/config";
import { adminRequest } from "../../../utils/requestMethods";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLocation, useNavigate } from "react-router-dom";
import { FaArrowLeftLong } from "react-icons/fa6";
import Loader from "../../../components/Loader/Loader";

const EditNotice = () => {
  const initialFormData = {
    title: "",
    desc: "",
    date: "",
    noticeCategoryId: "",
    file: null,
    notifier: [],
  };

  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [noticeCategory, setNoticeCategory] = useState([]);
  const [notifierList, setNotifierList] = useState([]);
  const [fileURL, setFileURL] = useState(null);

  const location = useLocation();
  const activeURL = location.pathname.split("/")[4];
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleImageChange = (e) => {
    setFormData({ ...formData, file: e.target.files[0] });
  };

  const handleNotifierChange = (e) => {
    const { value, checked } = e.target;
    if (checked) {
      setFormData((prev) => ({
        ...prev,
        notifier: [...prev.notifier, { name: value }],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        notifier: prev.notifier.filter((n) => n.name !== value),
      }));
    }
  };

  useEffect(() => {
    const fetchNotice = async () => {
      try {
        const response = await adminRequest.post(`${BASE_URL}/notice/view`, {
          uuid: activeURL,
        });

        const notice = response.data.notice;
        console.log(notice);
        setFormData({
          title: notice.title || "",
          desc: notice.desc || "",
          date: notice.date || "",
          noticeCategoryId: notice.notice_category_id || "",
          file: null,
          notifier: notice.Notifiers || [],
        });

        setFileURL(notice.fileURL || null);
      } catch (error) {
        console.error("Error fetching notice:", error);
        toast.error("Failed to load notice data.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchNotice();
  }, [activeURL]);

  useEffect(() => {
    const fetchNoticeCategories = async () => {
      try {
        const response = await adminRequest.get(
          `${BASE_URL}/notice/category/listActive`,
        );
        setNoticeCategory(response.data.data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchNoticeCategories();
  }, []);

  useEffect(() => {
    const fetchNotifierList = async () => {
      try {
        const response = await adminRequest.get(
          `${BASE_URL}/notifier/listActive`,
        );
        setNotifierList(response.data.data);
      } catch (error) {
        console.error("Error fetching notifiers:", error);
      }
    };
    fetchNotifierList();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formDataToSend = new FormData();

    if (formData.file) {
      formDataToSend.append("file", formData.file);
    }

    formDataToSend.append(
      "notice",
      JSON.stringify({
        uuid: activeURL,
        title: formData.title,
        desc: formData.desc,
        date: formData.date,
        noticeCategoryId: formData.noticeCategoryId,
        notifier: formData.notifier,
      }),
    );

    try {
      const response = await toast.promise(
        adminRequest.post(`${BASE_URL}/notice/update`, formDataToSend),
        {
          pending: "Updating notice...",
        },
      );

      toast.success(response.data.message || "Notice updated successfully", {
        autoClose: 800,
        onClose: () => navigate(-1),
      });

      setFormData(initialFormData);
    } catch (error) {
      toast.error(error.response?.data?.message || "Update failed.");
      console.error("Submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) return <Loader />;

  return (
    <div className="createNoticeContainer">
      <div className="top">
        <span className="backIcon" onClick={handleBackClick}>
          <FaArrowLeftLong />
        </span>
        <span className="headerTitle">Edit Notice</span>
      </div>

      <div className="bottom">
        <form onSubmit={handleSubmit}>
          <div className="noticeWrapper">
            <div className="inputGroup">
              <label htmlFor="title">Title</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                id="title"
                required
              />
            </div>

            <div className="inputGroup">
              <label htmlFor="noticeCategoryId">Notice Category</label>
              <select
                name="noticeCategoryId"
                value={formData.noticeCategoryId}
                onChange={handleChange}
                id="noticeCategoryId"
                required
              >
                <option value="" disabled>
                  Select Notice Category
                </option>
                {noticeCategory.map((nc) => (
                  <option key={nc.id} value={nc.id}>
                    {nc.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="inputGroup">
              <label htmlFor="date">Date</label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                id="date"
                required
              />
            </div>

            <div className="inputGroup">
              <label htmlFor="file">File</label>
              <input
                type="file"
                name="file"
                onChange={handleImageChange}
                id="file"
              />
              {fileURL && (
                <small>
                  Current file:{" "}
                  <a
                    href={`${IMG_URL}${fileURL}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    View
                  </a>
                </small>
              )}
            </div>

            <div className="inputGroup">
              <label htmlFor="desc">Description</label>
              <textarea
                name="desc"
                value={formData.desc}
                onChange={handleChange}
                id="desc"
                required
              />
            </div>

            <div className="inputGroup">
              <fieldset>
                <legend>Select Audience</legend>
                {notifierList.map((notif) => (
                  <label key={notif.name} className="checkbox">
                    <input
                      type="checkbox"
                      value={notif.name}
                      checked={
                        Array.isArray(formData.notifier) &&
                        formData.notifier.some((n) => n.name === notif.name)
                      }
                      onChange={handleNotifierChange}
                    />
                    {notif.name}
                  </label>
                ))}
              </fieldset>
            </div>
          </div>

          <div className="submitWrapper">
            <button
              type="submit"
              className="create-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
            <button
              type="button"
              className="cancel-btn"
              onClick={handleBackClick}
              disabled={isSubmitting}
            >
              Cancel
            </button>
          </div>
        </form>
        <ToastContainer position="top-center" />
      </div>
    </div>
  );
};

export default EditNotice;
